import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import './ProgressRing.scss'

/**
 * ProgressRing - SVG-based circular progress indicator
 *
 * @param {number} value - Current progress value
 * @param {number} max - Maximum value (default 100)
 * @param {number} size - Ring size in pixels
 * @param {number} strokeWidth - Ring stroke width
 * @param {string} color - Progress color
 * @param {string} bgColor - Background ring color
 * @param {string} label - Text label below percentage
 * @param {boolean} showPercentage - Show percentage in center
 * @param {boolean} animate - Animate on mount
 * @param {boolean} loading - Show loading state
 */
const ProgressRing = ({
	value = 0,
	max = 100,
	size = 120,
	strokeWidth = 10,
	color = 'var(--color-primary)',
	bgColor = 'var(--color-gray-200)',
	label,
	showPercentage = true,
	animate = true,
	loading = false
}) => {
	const [progress, setProgress] = useState(animate ? 0 : value)

	useEffect(() => {
		if (animate) {
			const timer = setTimeout(() => {
				setProgress(value)
			}, 100)
			return () => clearTimeout(timer)
		} else {
			setProgress(value)
		}
	}, [value, animate])

	const normalizedValue = Math.min(max, Math.max(0, progress))
	const percentage = Math.round((normalizedValue / max) * 100)

	const radius = (size - strokeWidth) / 2
	const circumference = radius * 2 * Math.PI
	const strokeDashoffset = circumference - (percentage / 100) * circumference

	const getColorByValue = () => {
		if (color !== 'auto') return color
		if (percentage >= 80) return 'var(--color-success)'
		if (percentage >= 50) return 'var(--color-warning)'
		return 'var(--color-danger)'
	}

	if (loading) {
		return (
			<div className="progress-ring" style={{ width: size, height: size }}>
				<div className="progress-ring__skeleton">
					<div className="skeleton skeleton--ring" style={{ width: size, height: size }}></div>
				</div>
			</div>
		)
	}

	return (
		<div className="progress-ring" style={{ width: size, height: size }}>
			<svg width={size} height={size} className="progress-ring__svg">
				{/* Background circle */}
				<circle
					className="progress-ring__bg"
					stroke={bgColor}
					fill="transparent"
					strokeWidth={strokeWidth}
					r={radius}
					cx={size / 2}
					cy={size / 2}
				/>
				{/* Progress circle */}
				<circle
					className="progress-ring__progress"
					stroke={getColorByValue()}
					fill="transparent"
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={`${circumference} ${circumference}`}
					strokeDashoffset={strokeDashoffset}
					r={radius}
					cx={size / 2}
					cy={size / 2}
					style={{
						transition: animate ? 'stroke-dashoffset 0.5s ease-out' : 'none'
					}}
				/>
			</svg>
			<div className="progress-ring__content">
				{showPercentage && (
					<span className="progress-ring__percentage">{percentage}%</span>
				)}
				{label && <span className="progress-ring__label">{label}</span>}
			</div>
		</div>
	)
}

ProgressRing.propTypes = {
	value: PropTypes.number,
	max: PropTypes.number,
	size: PropTypes.number,
	strokeWidth: PropTypes.number,
	color: PropTypes.string,
	bgColor: PropTypes.string,
	label: PropTypes.string,
	showPercentage: PropTypes.bool,
	animate: PropTypes.bool,
	loading: PropTypes.bool
}

export default ProgressRing
